import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import TickerBar from './components/TickerBar';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import About from './pages/About';
import TeamMember from './pages/TeamMember';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import Tools from './pages/Tools';
import Contact from './pages/Contact';
import Login from './pages/Signin';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import AuthCallback from './pages/AuthCallback';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminEntry from './pages/AdminEntry';
import { initAdminSession } from './services/adminSession';
import UserDashboard from './pages/UserDashboard';
import PaperTrading from './pages/PaperTrading';
import Blog from './pages/Blog/Blog';
import BlogDetail from './pages/Blog/BlogDetail';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import WhatsAppButton from './components/WhatsAppButton';

function RedirectToCourses() {
  return <Navigate to="/courses" replace />;
}

function LegacySlugRedirectToCourses() {
  const { slug } = useParams();
  return <Navigate to={`/courses/${slug}`} replace />;
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return isAuthenticated ? children : <Navigate to="/signin" replace />;
}

function AppContent({ theme, setTheme }) {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');
  const isAuthPage = location.pathname === '/signin' || location.pathname === '/signup' || location.pathname === '/forgotpassword';
  const isDashboard = location.pathname === '/dashboard';
  const isPaperTrading = location.pathname === '/tools/papertrading';
  const showChrome = !isAdminPage && !isAuthPage && !isDashboard && !isPaperTrading;

  return (
    <div className="App">
      {showChrome && <TickerBar />}
      {showChrome && <Navbar theme={theme} setTheme={setTheme} />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/about/team/:slug" element={<TeamMember />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/:id" element={<CourseDetail />} />
          <Route path="/programs" element={<RedirectToCourses />} />
          <Route path="/programs/:slug" element={<LegacySlugRedirectToCourses />} />
          <Route path="/services" element={<RedirectToCourses />} />
          <Route path="/tools" element={<Tools />} />
          <Route path="/weekly-outlook" element={<Navigate to="/blog" replace />} />
          <Route path="/market-outlook" element={<Navigate to="/blog" replace />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacypolicy" element={<PrivacyPolicy />} />
          <Route path="/termsofservice" element={<TermsOfService />} />
          <Route path="/signin"        element={<Login theme={theme} setTheme={setTheme} />} />
          <Route path="/signup"        element={<Signup theme={theme} setTheme={setTheme} />} />
          <Route path="/forgotpassword" element={<ForgotPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/admin" element={<AdminEntry />} />
          <Route path="/admin/signin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/contactmessages" element={<AdminDashboard />} />
          <Route path="/admin/messages" element={<Navigate to="/admin/contactmessages" replace />} />
          <Route path="/admin/blogs"     element={<AdminDashboard />} />
          <Route path="/admin/users"     element={<AdminDashboard />} />
          <Route path="/admin/courses"   element={<AdminDashboard />} />
          <Route path="/admin/payments"  element={<AdminDashboard />} />
          {/* Paper Trading admin group. These were lost when 85e7862 gutted
              AdminDashboard.js; without a route each, the section falls back to
              'dashboard' and the nav item looks broken. */}
          <Route path="/admin/tools"           element={<AdminDashboard />} />
          <Route path="/admin/stocks"          element={<AdminDashboard />} />
          <Route path="/admin/marketdata"      element={<AdminDashboard />} />
          <Route path="/admin/tradingactivity" element={<AdminDashboard />} />
          <Route path="/admin/adminusers" element={<AdminDashboard />} />
          <Route path="/admin/profile"   element={<AdminDashboard />} />
          <Route path="/admin/*" element={<AdminEntry />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <UserDashboard />
              </ProtectedRoute>
            }
          />
          {/* Restored from 4280c79 — removed by 85e7862, which left the page
              component in place with no route, so /tools/papertrading fell
              through to the SPA catch-all and rendered an empty shell. */}
          <Route
            path="/tools/papertrading"
            element={
              <ProtectedRoute>
                <PaperTrading theme={theme} setTheme={setTheme} />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
      {showChrome && <WhatsAppButton />}
      {showChrome && <Footer theme={theme} />}
    </div>
  );
}

function App() {
  const [theme, setTheme] = useState('dark');

  useEffect(() => {
    initAdminSession();
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <ScrollToTop />
        <AppContent theme={theme} setTheme={setTheme} />
      </AuthProvider>
    </Router>
  );
}

export default App;
